// Moldura das telas da equipe: cabecalho fixo com cronometro e navegacao.
//
// A assinatura do SSE vive aqui, e nao em cada pagina: uma conexao por aba,
// e qualquer tela aberta recebe as atualizacoes.

import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ContestStateDto, ScoreboardDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { useContestClock, useEventStream, useSession } from "../../lib/hooks.js";
import {
  ConnectionDot,
  ContestStatusBadge,
  Countdown,
  FrozenBanner,
} from "../../components/ui.js";

export function TeamLayout(): React.JSX.Element {
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const contest = useQuery({
    queryKey: ["contest"],
    queryFn: () => api.get<ContestStateDto>("/contest"),
    refetchInterval: 60_000,
  });

  const connection = useEventStream("/api/stream", {
    onContest: (state) => queryClient.setQueryData(["contest"], state),
    onScoreboard: (board: ScoreboardDto) =>
      queryClient.setQueryData(["scoreboard"], board),
    onSubmission: () => {
      // O historico e o status dos problemas dependem das submissoes.
      void queryClient.invalidateQueries({ queryKey: ["submissions"] });
    },
  });

  const elapsed = useContestClock(contest.data);

  const logout = async (): Promise<void> => {
    await api.post("/auth/logout");
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-slate-900">
              {session.data?.name ?? "Equipe"}
            </span>
            <ConnectionDot state={connection} />
          </div>

          <nav className="flex gap-1 text-sm">
            <Tab to="/problemas">Problemas</Tab>
            <Tab to="/submissoes">Submissoes</Tab>
            <Tab to="/placar">Placar</Tab>
          </nav>

          <div className="ml-auto flex items-center gap-4">
            {contest.data && (
              <>
                <ContestStatusBadge status={contest.data.status} />
                <Countdown
                  elapsed={elapsed}
                  duration={contest.data.durationSeconds}
                  status={contest.data.status}
                />
              </>
            )}
            <button
              onClick={() => void logout()}
              className="text-sm text-slate-500 hover:text-slate-900 hover:underline"
            >
              Sair
            </button>
          </div>
        </div>

        {contest.data?.frozen && (
          <div className="bg-sky-50 px-4 py-2">
            <div className="mx-auto max-w-6xl">
              <FrozenBanner />
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function Tab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded px-3 py-1.5 font-medium transition-colors ${
          isActive
            ? "bg-slate-900 text-white"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
